import { useState, useCallback } from 'react'
import { connectGoogleGmail, disconnectService, getConnections } from './api'
import type { ConnectionInfo } from './api'
import type { SettingsData } from './settings-types'

export function useConnectionManager() {
  const [connections, setConnections] = useState<ConnectionInfo[]>([])
  const [connectingService, setConnectingService] = useState<string | null>(null)

  const fetchConnections = useCallback(async (): Promise<ConnectionInfo[] | null> => {
    try {
      const result = await getConnections()
      setConnections(result.connections)
      return result.connections
    } catch (err) {
      console.warn('[Settings] Could not fetch connections:', err)
      return null
    }
  }, [])

  const handleConnectGmail = useCallback(async (): Promise<void> => {
    setConnectingService('gmail')
    try {
      const result = await connectGoogleGmail()
      window.location.href = result.authUrl
    } catch (err) {
      console.error('Failed to start Gmail connection:', err)
    } finally {
      setConnectingService(null)
    }
  }, [])

  const handleDisconnect = useCallback(async (
    _itemId: string,
    _data: SettingsData | null,
    _setData: (d: SettingsData) => void,
  ): Promise<void> => {
    const connection = connections.find(c => c.status === 'active')
    if (!connection) return

    try {
      await disconnectService(connection.connectionId)
      setConnections(prev => prev.filter(c => c.connectionId !== connection.connectionId))
    } catch (err) {
      console.error('Failed to disconnect:', err)
    }
  }, [connections])

  /** No-op — Google connection state is now managed by Plugins screen */
  const mergeConnectionState = useCallback((settings: SettingsData, _connections: ConnectionInfo[]): SettingsData => {
    return settings
  }, [])

  return {
    connections,
    connectingService,
    fetchConnections,
    handleConnectGmail,
    handleDisconnect,
    mergeConnectionState,
  }
}
